import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NotificationService } from '../../../core/services/notification.service';
import { finalize, takeUntil, debounceTime } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import {
  Notification,
  NotificationPreference,
} from '../../../core/models/notification.model';
import { LoaderService } from '../../../core/services/loader.service';
import { Observable, of, Subject } from 'rxjs';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { AuthService } from '../../../core/services/auth.service';
import { OrganizationDetailModel } from '../../../features/organizations/organization.interface';
import { CommonService } from '../../../shared/services/common.service';
import { DrawerService } from '../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';

@Component({
  selector: 'app-notifications-settings',
  standalone: true,
  imports: [
    NzTableModule,
    CommonModule,
    NzSwitchModule,
    FormsModule,
    NzInputNumberModule,
  ],
  templateUrl: './notifications-settings.component.html',
  styleUrl: './notifications-settings.component.scss',
})
export class NotificationsSettingsComponent implements OnInit, OnDestroy {
  private commonService = inject(CommonService);

  listOfColumn = [
    { title: 'Notification Types' },
    { title: 'In App' },
    { title: 'Email' },
  ];
  listOfData$: Observable<Notification[]>;

  orgData!: OrganizationDetailModel;
  selectedOrganizationId: number | null = null;
  isSuperAdmin = false;
  isPrefSettingsAdmin = false;

  private readonly destroy$ = new Subject<void>();
  private thresholdUpdate$ = new Subject<{
    item: Notification;
    value: number;
  }>();

  loading$: Observable<boolean> = of(false);

  constructor(
    private notificationService: NotificationService,
    private readonly loaderService: LoaderService,
    private authService: AuthService,
    private drawerService: DrawerService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.listOfData$ = this.notificationService.notificationSettings$;
    this.subscribeToDrawerParams();
  }

  ngOnInit(): void {
    this.subscribeToOrgData();
    this.getNotificationPreferences();
    this.setupDebounceForThresholdUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDebounceForThresholdUpdates(): void {
    this.thresholdUpdate$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(({ item, value }) => {
        this.updateThresholdValue(item, value);
      });
  }

  private subscribeToOrgData(): void {
    this.authService.orgData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(organization => {
        if (organization) {
          this.orgData = organization;
          if (this.selectedOrganizationId) {
            this.orgData.organizationId = this.selectedOrganizationId;
          }
          this.isPrefSettingsAdmin =
            this.orgData.user.roles.includes('District Settings Admin') ||
            false;
        }
      });

    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (user) {
          this.isSuperAdmin = user.isSuperAdmin;
          this.isPrefSettingsAdmin = user.isSuperAdmin;
        }
      });
  }

  getNotificationPreferences(): void {
    const orgIdToUse =
      this.selectedOrganizationId || this.orgData?.organizationId;

    this.loaderService.show();
    const notificationCall = orgIdToUse
      ? this.notificationService.getNotifications(orgIdToUse)
      : this.notificationService.getNotifications();

    notificationCall
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        error: () => {
          this.commonService.notify(
            'error',
            'Failed to load notification preferences'
          );
        },
      });
  }

  updateThreshold(item: Notification, value: number): void {
    if (value < 0) {
      this.commonService.notify('error', 'Threshold value cannot be negative');
      return;
    }
    item.thresholdDays = value;
    this.thresholdUpdate$.next({ item, value });
  }

  onBlurThreshold(item: Notification, value: number): void {
    if (value < 0) {
      this.commonService.notify('error', 'Threshold value cannot be negative');
      return;
    }
    this.updateThresholdValue(item, value);
  }

  private updateThresholdValue(item: Notification, value: number): void {
    const originalValue = item.thresholdDays;
    this.loaderService.show();
    const payload = {
      notificationTypeId: item.id,
      thresholdDays: value,
    };
    this.notificationService
      .updateThresholdDays(payload)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Threshold value updated successfully'
          );
        },
        error: () => {
          item.thresholdDays = originalValue;
          this.commonService.notify(
            'error',
            'Failed to update threshold value'
          );
        },
      });
  }

  toggleInAppNotification(item: Notification): void {
    this.loaderService.show();
    const orgIdToUse =
      this.selectedOrganizationId || this.orgData?.organizationId;

    const payload: NotificationPreference = {
      notificationTypeId: item.id,
      organizationId: orgIdToUse!,
      userId: this.orgData?.user?.userId,
      inApp: item.inApp,
      inEmail: item.inEmail,
    };

    // For super admin without organization context, don't pass organizationId
    if (!orgIdToUse && this.isSuperAdmin) {
      delete (payload as Partial<NotificationPreference>).organizationId;
    }

    this.notificationService
      .createNotificationPreference(payload)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'In-app notification setting updated'
          );
        },
        error: () => {
          item.inApp = !item.inApp;
          this.commonService.notify(
            'error',
            'Failed to update notification settings'
          );
        },
      });
  }

  toggleEmailNotification(item: Notification): void {
    this.loaderService.show();
    const orgIdToUse =
      this.selectedOrganizationId || this.orgData?.organizationId;

    const payload: NotificationPreference = {
      notificationTypeId: item.id,
      organizationId: orgIdToUse!,
      userId: this.orgData?.user?.userId,
      inApp: item.inApp,
      inEmail: item.inEmail,
    };

    // For super admin without organization context, don't pass organizationId
    if (!orgIdToUse && this.isSuperAdmin) {
      delete (payload as Partial<NotificationPreference>).organizationId;
    }

    this.notificationService
      .createNotificationPreference(payload)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            'Email notification setting updated'
          );
        },
        error: () => {
          item.inEmail = !item.inEmail;
          this.commonService.notify(
            'error',
            'Failed to update notification settings'
          );
        },
      });
  }
  private subscribeToDrawerParams(): void {
    this.drawerService
      .getParams<{ selectedOrganizationId: number | null }>(
        DRAWER_IDS.NOTIFICATION_SETTINGS
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params && params.selectedOrganizationId !== undefined) {
          this.selectedOrganizationId = params.selectedOrganizationId;
          this.getNotificationPreferences();
        }
      });
  }
}
